import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { ChangeEvent, useState } from "react";
import { useFormContext } from "react-hook-form";
import { get, isEmpty } from "lodash";
import classNames from "classnames";
import { LTooltip } from "./LTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const LInputField = ({ ...props }: InputFieldProps): JSX.Element => {
    const {
        register,
        formState: { errors }
    } = useFormContext();
    const error = get(errors, props.inputName);
    const hasError = !isEmpty(error);
    const fieldError = error?.message?.toString() ?? "";
    const [fieldValue, setFieldValue] = useState(props.defaultValue ?? "");

    const classes = classNames(
        "input",
        props.isExpanded ? "is-expanded" : "",
        hasError ? "is-danger" : "",
        props.isRounded ? "is-rounded" : ""
    );
    const labelClasses = classNames(
        "label",
        "is-small",
        hasError ? "is-danger" : "",
        props.theme === "dark" ? "has-text-white" : ""
    );

    const isNumberInput = (e: ChangeEvent<HTMLInputElement>): void => {
        const reg = /^[0-9]+$/;
        if (props.isNumber && !reg.test(e.target.value)) {
            setFieldValue(e.target.value.replace(/[^0-9]/g, ""));
        } else {
            setFieldValue(e.target.value);
        }
    };

    return (
        <div className="field">
            {props.label && (
                <label className={labelClasses} style={{ ...(props.labelColor && { color: props.labelColor }) }}>
                    {props.label}{" "}
                    {props.tooltip && (
                        <span className="ml-2">
                            <LTooltip text={props.tooltip} direction="downRight" />
                        </span>
                    )}
                </label>
            )}
            <div className={`control${props.isSearch ? " has-icons-left" : ""}`}>
                {props.type === "file" ? (
                    <div className="file is-primary">
                        <label className="file-label">
                            <input
                                className="file-input"
                                type="file"
                                multiple={false}
                                {...register(props.inputName, { onChange: props.onChange })}
                            />
                            <span className="file-cta">
                                <span className="file-label">{props.placeholder}</span>
                            </span>
                        </label>
                    </div>
                ) : (
                    <input
                        type={props.type ?? "text"}
                        className={classes}
                        placeholder={props.placeholder}
                        value={fieldValue}
                        maxLength={props.maxLength}
                        onInput={isNumberInput}
                        {...register(props.inputName, { onChange: props.onChange })}
                        disabled={props.disabled}
                    />
                )}
                {props.isSearch && (
                    <span className="icon is-left">
                        <FontAwesomeIcon icon={faSearch} size={"sm"} />
                    </span>
                )}
            </div>
            {!props.hideErrorLabel && (
                <p className={`help is-danger ${hasError ? "" : "is-hidden-custom"}`}>{fieldError || "*"}</p>
            )}
        </div>
    );
};

export interface InputFieldProps extends FormFieldProps {
    type?: "email" | "text" | "password" | "number" | "time" | "file";
    placeholder?: string;
    isSmall?: boolean; // Specifically for input group
    isLargeLabel?: boolean;
    isExpanded?: boolean;
    isNumber?: boolean;
    theme?: "dark" | "light";
    isRounded?: boolean;
    tooltip?: string;
    disabled?: boolean;
    hideErrorLabel?: boolean;
    isSearch?: boolean;
    labelColor?: string;
}

export interface FormFieldProps {
    /** The string label */
    label?: string;
    /** The HTML input `name` attribute */
    inputName: string;
    /** The initial value */
    value?: string;
    /** Sets the initial value of the input */
    defaultValue?: string;
    /** HTML input `onChange` attribute */
    onChange?: React.ChangeEventHandler;
    /** Maximum amount of characters a field will accept */
    maxLength?: number;
}
